import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import BlockIcon from '@material-ui/icons/Block'
import SydButton from '../../../../../../commonDesign/Button'
import { postNamedQuery } from '../../../../../../../service'
import { flattenNoFollow } from '../../../../../../../utils/flatten'
import { dataPipelineVersioning } from '../../../../../../../api/coreData'
import { useEditor } from './EditorContext'
import VariablesForm from './VariablesForm'
import ResultsView from './ResultsView'

// VERBATIM COPY of the legacy analysis derivation from
// PipelineWorkspaceContext.jsx's `useEffect(...[response, responseError])`.
// Keep these two implementations byte-identical inside the reduce/map so
// downstream consumers (Data Marts, addPipelineToDataCatalog) cannot drift.
// Wrapped in a function instead of an effect so the test-run flow can call
// it imperatively and short-circuit when there's nothing to derive.
function deriveAnalysisFromResult (response, responseError) {
  if (responseError || !response?.data?.length) return null
  const data = response.data
  const allColumns = data.reduce((p, c) => {
    const cFlat = flattenNoFollow(c)
    const fields = Object.entries(cFlat)
    fields.forEach(([key, value]) => {
      if (key in p && p[key] !== 'null') return
      if (value === null) {
        p[key] = 'null'
        return
      }
      p[key] = typeof value
    })
    return p
  }, {})
  const fields = Object.entries(allColumns).map(([key, type]) => ({
    accessor: key,
    type
  }))
  return { fields }
}

const useStyles = makeStyles(() => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  header: {
    flexShrink: 0,
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-neutral-200)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: 'var(--color-neutral-050)'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  clearCacheBtn: {
    padding: 4,
    color: 'var(--color-neutral-400)',
    '&:hover': {
      color: 'var(--color-warning-600, #d97706)',
      backgroundColor: 'var(--color-warning-050, #fffbeb)'
    },
    '&:disabled': { color: 'var(--color-neutral-200)' }
  },
  clearCacheBtnConfirm: {
    color: 'var(--color-warning-700, #b45309)',
    backgroundColor: 'var(--color-warning-050, #fffbeb)'
  },
  variablesWrap: {
    flexShrink: 0,
    maxHeight: '40%',
    overflowY: 'auto',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  resultsWrap: {
    flex: 1,
    minHeight: 0
  }
}))

function TestPanel ({ pipelineCode }) {
  const classes = useStyles()
  const {
    variables, setVariables,
    pipelineParse,
    testResult, setTestResult,
    testError, setTestError,
    testRunning, setTestRunning,
    setAnalysis
  } = useEditor()

  const clearCache = dataPipelineVersioning.useClearDataPipelineCache()
  const [confirmClear, setConfirmClear] = useState(false)
  const confirmTimerRef = useRef(null)
  const [toast, setToast] = useState(null)

  useEffect(() => () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
  }, [])

  const handleClearCache = useCallback(async () => {
    if (!pipelineCode) return
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current)
      confirmTimerRef.current = null
    }
    if (!confirmClear) {
      setConfirmClear(true)
      confirmTimerRef.current = setTimeout(() => {
        setConfirmClear(false)
        confirmTimerRef.current = null
      }, 3000)
      return
    }
    setConfirmClear(false)
    try {
      const result = await clearCache.mutateAsync({ code: pipelineCode })
      const n = result?.deletedCount
      setToast({
        severity: 'success',
        message: n != null
          ? `Cleared ${n} cached result${n === 1 ? '' : 's'} for "${pipelineCode}"`
          : `Cleared cached results for "${pipelineCode}"`
      })
    } catch (err) {
      setToast({
        severity: 'error',
        message: err?.response?.data?.message || err.message || 'Failed to clear cache'
      })
    }
  }, [pipelineCode, confirmClear, clearCache])

  const runTest = useCallback(async () => {
    if (pipelineParse.error) {
      setTestError(`Pipeline JSON parse error: ${pipelineParse.error}`)
      setTestResult(null)
      return
    }
    setTestRunning(true)
    setTestError(null)
    setTestResult(null)
    try {
      const components = Array.isArray(pipelineParse.value)
        ? pipelineParse.value
        : (pipelineParse.value?.components || [])
      const r = await postNamedQuery('coreData', 'execute-data-pipeline', {
        pipeline: components,
        variables
      })
      const body = r?.data ?? null
      setTestResult(body)
      const derived = deriveAnalysisFromResult(body, null)
      if (derived) setAnalysis(derived)
    } catch (err) {
      setTestError(err?.response?.data?.message || err.message || 'Execution failed')
    } finally {
      setTestRunning(false)
    }
  }, [pipelineParse, variables, setTestRunning, setTestError, setTestResult, setAnalysis])

  const clearDisabled = !pipelineCode || clearCache.isLoading
  const clearTooltip = !pipelineCode
    ? 'Save the pipeline before clearing its cache'
    : confirmClear
      ? 'Click again to confirm'
      : 'Clear cached results for this pipeline'

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <span>Test</span>
        <div className={classes.headerActions}>
          <Tooltip title={clearTooltip} arrow>
            <span>
              <IconButton
                size='small'
                className={`${classes.clearCacheBtn} ${confirmClear ? classes.clearCacheBtnConfirm : ''}`}
                onClick={handleClearCache}
                disabled={clearDisabled}
              >
                <BlockIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <SydButton
            size='xs'
            variant='secondary'
            icon='arrowForward'
            onClick={runTest}
            processing={testRunning}
            disabled={testRunning || !!pipelineParse.error}
          >
            Run
          </SydButton>
        </div>
      </div>
      <div className={classes.variablesWrap}>
        <VariablesForm value={variables} onChange={setVariables} />
      </div>
      <div className={classes.resultsWrap}>
        <ResultsView result={testResult} error={testError} />
      </div>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </div>
  )
}

TestPanel.propTypes = {
  pipelineCode: PropTypes.string
}

export default TestPanel
